import { appStore } from '../app/AppStore';
import { SessionSiteSelectors } from '../app/selectors/SessionInfoSelectors';
import { SiteSpaceConfigHelper } from '../app/spaces/SpaceConfig/SiteSpaceConfigHelper';
import { selectCatalogingOnSiteEnabled } from '../catalog/selectors/CatalogSelectors';
import { UserSettingsActions } from './actions/UserSettingsActions';
import { UserStateParams } from './config';
import { DataRolePlaceCtrl } from './controllers/DataRolePlaceCtrl';
import { DatasourcePlaceCtrl } from './controllers/DatasourcePlaceCtrl';
import { DatasourcePublishResultCtrl } from './controllers/DatasourcePublishResultCtrl';
import { DatasourcesCtrl, DatasourcesTemplate } from './controllers/DatasourcesCtrl';
import { IErrorStateParams } from './controllers/ErrorCtrl';
import { FavoritesSpaceCtrl } from './controllers/FavoritesSpaceCtrl';
import { FlowPlaceCtrl } from './controllers/FlowPlaceCtrl';
import { FlowPublishResultCtrl } from './controllers/FlowPublishResultCtrl';
import { FlowsCtrl } from './controllers/FlowsCtrl';
import { MetricsCtrl } from './controllers/MetricsCtrl';
import { MetricsSpaceCtrl } from './controllers/MetricsSpaceCtrl';
import { NaturalLanguageCtrl } from './controllers/NaturalLanguageCtrl';
import { ProjectPlaceCtrl } from './controllers/ProjectPlaceCtrl';
import { RecentsSpaceCtrl } from './controllers/RecentsSpaceCtrl';
import { RecommendationsSpaceCtrl } from './controllers/RecommendationsSpaceCtrl';
import { setBrowserTitleCtrl } from './controllers/SetBrowserTitleCtrl';
import { SitePlaceCtrl } from './controllers/SitePlaceCtrl';
import { SiteUserPlaceCtrl } from './controllers/SiteUserPlaceCtrl';
import { SiteUserSettingsSubplaceCtrl } from './controllers/SiteUserSettingsSubplaceCtrl';
import { SubscriptionsCtrl } from './controllers/SubscriptionsCtrl';
import { FeatureFlags } from './FeatureFlags';
import { Filter } from './helpers/Filter';
import { Helpers } from './helpers/Helpers';
import { SubplaceStateConfigHelper } from './helpers/SubplaceStateConfigHelper';
import {
  DatasourceContentType,
  FlowContentType,
  ProjectContentType,
  ViewContentType,
  WorkbookContentType,
} from './models/ContentTypes';
import { FlowListItemInfo } from './models/FlowInfo';
import { FlowPlace } from './models/FlowPlace';
import { IDataRoleInfo } from './models/IDataRoleInfo';
import { IDataSourceDetailInfo } from './models/IDataSourceInfo';
import { Place } from './models/IPlace';
import { IUserInfo } from './models/IUserInfo';
import { IViewBase } from './models/IViewInfo';
import { SiteUserPlace } from './models/SiteUserPlace';
import { Module } from './Module';
import { DataConnections } from './resources/DataConnections';
import { DataRoles } from './resources/Dataroles';
import { DataSources } from './resources/Datasources';
import { Favorites } from './resources/Favorites';
import { Flows } from './resources/Flows';
import { Projects } from './resources/Projects';
import { ServerUsers } from './resources/ServerUsers';
import { SiteUsers } from './resources/SiteUsers';
import { Users } from './resources/Users';
import { Views } from './resources/Views';
import { Workbooks } from './resources/Workbooks';
import { DatasourceIdToConnectionIdsMap } from './ServerApi/types/DatasourceIdToConnectionIdsMap';
import { ISiteUser } from './ServerApi/types/ISiteUser';
import { DefaultStateFor } from './services/ContentUrl';
import { ErrorState } from './services/ErrorState';
import { AuthLevel, ServerService } from './services/ServerService';
import { SiteUrlMatcherFactory } from './services/SiteUrlMatcherFactory';
import { StartPageService } from './services/StartPageService';

interface IDetailContentParams extends ng.ui.IStateParamsService {
  id: string;
}

interface ISiteStateParams extends ng.ui.IStateParamsService {
  siteUrlName: string;
}

Module.instance().config(<any[]>[
  '$stateProvider',
  '$urlRouterProvider',
  'SiteUrlMatcherFactoryProvider',
  (
    $stateProvider: ng.ui.IStateProvider,
    $urlRouterProvider: ng.ui.IUrlRouterProvider,
    siteUrlMatcherFactory: SiteUrlMatcherFactory) => {
    const siteUrl = (url: string) => siteUrlMatcherFactory.compile(url);

    /**
     * This function creates a redirect handler for a site url. See $urlRouterProvider documentation for details.
     * The general use case for this function is to redirect differently based on feature flags.
     */
    function customSiteUrlRedirect(getRedirectedUrl: ($match: any) => string | false) {
      return [ '$match', ($match: any) => {
        const redirectedUrl = getRedirectedUrl($match);
        // If the redirected URL is false, don't redirect
        if (redirectedUrl === false) {
          return false;
        }
        return siteUrl(redirectedUrl).format($match);
      }];
    }

    /**
     * A simple URL redirect handler, which redirects to a static URL.
     */
    function siteUrlRedirect(url: string) {
      return customSiteUrlRedirect(() => url);
    }

    const rootSiteUrlRedirect =
    ['$match',
    'StartPageService',
    '$location',
      ($match: ISiteStateParams,
        startPageService: StartPageService,
        $location: ng.ILocationService) => {
        const noSiteInUrl = !$match.siteUrlName;
        const params = Helpers.serialize($location.search());
        const startPage = (noSiteInUrl)
            // When going to the root url ('/') go to the start page for the site we are currently on.
            ? startPageService.getCurrentUserStartPageForCurrentSite()
            // When going to the root url for an explicit site ('/site/foo'), go to the start page on that site
            : startPageService.getCurrentUserStartPageForSite($match.siteUrlName);
        return startPage + (params ? (/\?/.test(startPage) ? '&' : '?') + params : '');
      }
    ];

    // Redirects for default related content types
    // TODO: perhaps add these as part of state creation helper methods
    $urlRouterProvider
      .when(siteUrl(''), rootSiteUrlRedirect)
      .when(siteUrl('/'), rootSiteUrlRedirect)
      .when(siteUrl('/{type}/'), siteUrlRedirect('/{type}'))
      .when(siteUrl('/{type}/{id}/'), siteUrlRedirect('/{type}/{id}'))
      .when(siteUrl('/projects/{id}'), customSiteUrlRedirect(() => false))
      .when(siteUrl('/flows/{id}'), siteUrlRedirect('/flows/{id}/overview'))
      .when(siteUrl('/dataRoles/{id}'), siteUrlRedirect('/dataRoles/{id}/definition'))
      // With Spaces, the typical link to /datasources/{id} has been replaced by /explore/datasource/{id}.
      // Hence, the DefaultSubplaceForContentType logic in ContentUrl.ts must match the logic here in order
      // for the redirects to work consistently regardless of the entry URL for a data source.
      .when(siteUrl('/datasources/{id}'), customSiteUrlRedirect(() => {
        const tabName = SessionSiteSelectors.isNLPServiceEnabled(appStore.getState()) ? 'askData' : 'connections';
        return '/datasources/{id}/' + tabName;
      }));

    // The Integrated Datasources feature removed the "data connection" page for datasources and
    // replaced it with the "data connections" page. If any old links point to the old page, this
    // redirect will get them to the right place.
    $urlRouterProvider.when(siteUrl('/datasources/{id}/dataConnection'), siteUrlRedirect('/datasources/{id}/connections'));

    // No longer have a details url. Redirect in case anyone has it bookmarked.
    $urlRouterProvider.when(siteUrl('/datasources/{id}/details'), customSiteUrlRedirect(() => '/datasources/{id}'));

    // If someone tries to access the NLP page when the flag is disabled, redirect to the connections page.
    $urlRouterProvider.when(siteUrl('/datasources/{id}/askData'), customSiteUrlRedirect(() =>
    SessionSiteSelectors.isNLPServiceEnabled(appStore.getState()) ? false : '/datasources/{id}/connections'));

    // If someone tries to access the lineage page when the flag is disabled, redirect to the connections page.
    $urlRouterProvider.when(siteUrl('/datasources/{id}/lineage'), customSiteUrlRedirect(() =>
    selectCatalogingOnSiteEnabled(appStore.getState()) ? false : '/datasources/{id}/connections'));

    // If someone tries to access the Datasource Usage Analytics page when NLP flag is disabled/feature flag is off, redirect to the connections page.
    $urlRouterProvider.when(siteUrl('/datasources/{id}/askDataUsageAnalytics'), customSiteUrlRedirect(() =>
    (FeatureFlags.nldatasourceAnalyticsEnabled)? false : '/datasources/{id}/askData'));

    // If someone tries to access the lineage page when the flag is disabled, redirect to the views page.
    $urlRouterProvider.when(siteUrl('/workbooks/{id}/lineage'), customSiteUrlRedirect(() =>
    selectCatalogingOnSiteEnabled(appStore.getState()) ? false : '/workbooks/{id}/views'));

    // The Mixed Content feature removed all the subplaces under the Project place. If the user
    // tries to navigate to any subplace, redirect them to the main project place.
    $urlRouterProvider.when(siteUrl('/projects/{id}/{anySubplace}'), customSiteUrlRedirect(() => '/projects/{id}'));

    // if viz rec is not enabled, redirect to error state from recommendations url
    $urlRouterProvider
      .when(siteUrl('/recommendations'),
      ['ErrorState',
        (errorState: ErrorState) => {
          if (SessionSiteSelectors.isVizRecommendationsEnabled(appStore.getState())) {
            return false;
          } else {
            errorState.go({});
          }
      }]
    );

    // If dataRoles feature flag isn't on, redirect to error state from dataRoles url
    $urlRouterProvider.when(siteUrl('/dataRoles'),
    ['ErrorState',
      (errorState: ErrorState) => {
        if (FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled) { return false; }
        errorState.go({});
      }]
    );


    // The url /redirect_to_view is currently required by the React favorites menu.
    // TODO: perhaps expose the view's path on the IFavorite data structure to avoid this redirect.
    $urlRouterProvider
      .when(siteUrl('/redirect_to_view/{id}'),
        ['$match',    '$state',                    'ErrorState',           'Favorites',          'Views',
        ($match: any, $state: ng.ui.IStateService, errorState: ErrorState, favorites: Favorites, views: Views) => {
        views.getById($match.id).then(
          (viewInfo: IViewBase) => $state.go(DefaultStateFor(ViewContentType.name),
            { path: viewInfo.path }, { location: 'replace' }),
          () => {
            favorites.removeViewById($match.id);
            errorState.go({ detailsLocKey: 'favoriteViewCouldNotBeFound' });
          }
        );
      }])
      .when(siteUrl('/redirect_to_workbook/{id}'),
        ['$match',    '$state',                    'ErrorState',           'Favorites',          'Workbooks',
        ($match: any, $state: ng.ui.IStateService, errorState: ErrorState, favorites: Favorites, workbooks: Workbooks) => {
        workbooks.getById($match.id).then(
          () => $state.go(DefaultStateFor(WorkbookContentType.name), { id: $match.id, siteUrlName: $match.siteUrlName }, { location: 'replace' }),
          () => {
            favorites.remove($match.id, 'workbook');
            errorState.go({ detailsLocKey: 'favoriteWorkbookCouldNotBeFound' });
          }
        );
      }])
      .when(siteUrl('/redirect_to_project/{id}'),
        ['$match',    '$state',                    'ErrorState',           'Favorites',          'Projects',
        ($match: any, $state: ng.ui.IStateService, errorState: ErrorState, favorites: Favorites, projects: Projects) => {
        projects.getById($match.id).then(
          () => $state.go(DefaultStateFor(ProjectContentType.name), { id: $match.id, siteUrlName: $match.siteUrlName }, { location: 'replace' }),
          () => {
            favorites.remove($match.id, 'project');
            errorState.go({ detailsLocKey: 'favoriteProjectCouldNotBeFound' });
          }
        );
      }])
      .when(siteUrl('/redirect_to_datasource/{idForFavoriting}/{id}'),
        ['$match',    '$state',                    'ErrorState',           'Favorites',          'DataSources',
        ($match: any, $state: ng.ui.IStateService, errorState: ErrorState, favorites: Favorites, datasources: DataSources) => {
          datasources.getById($match.id).then(
            () => {
              $state.go(DefaultStateFor(DatasourceContentType.name), { id: $match.id, siteUrlName: $match.siteUrlName }, { location: 'replace' });
            },
            () => {
              favorites.remove($match.idForFavoriting, 'datasource');
              errorState.go({ detailsLocKey: 'favoriteDatasourceCouldNotBeFound' });
            }
          );
        }])
      .when(siteUrl('/redirect_to_removed_datasource/{idForFavoriting}'),
        ['$match',    'ErrorState',           'Favorites',
        ($match: any, errorState: ErrorState, favorites: Favorites) => {
          favorites.remove($match.idForFavoriting, 'datasource');
          errorState.go({ detailsLocKey: 'favoriteDatasourceCouldNotBeFound' });
        }
      ])
      .when(siteUrl('/redirect_to_flow/{id}'),
        ['$match',    '$state',                    'ErrorState',           'Favorites',          'Flows',
        ($match: any, $state: ng.ui.IStateService, errorState: ErrorState, favorites: Favorites, flows: Flows) => {
        flows.getById($match.id).then(
          () => $state.go(DefaultStateFor(FlowContentType.name), { id: $match.id, siteUrlName: $match.siteUrlName }, { location: 'replace' }),
          () => {
            favorites.remove($match.id, 'flow');
            errorState.go({ detailsLocKey: 'favoriteFlowCouldNotBeFound' });
          }
        );
      }])
      // This supports navigating to the datasource place page by the actual datasource ID, instead of the connection ID.
      .when(siteUrl('/explore/datasource/{id}'),
        ['$match',    '$state',                    'ErrorState',           'DataConnections',
        ($match: any, $state: ng.ui.IStateService, errorState: ErrorState, dataconnections: DataConnections) => {
          dataconnections.getConnectionIdsFromDatasourceIds([ $match.id ]).then(
            (map: DatasourceIdToConnectionIdsMap) => {
              $state.go(DefaultStateFor(DatasourceContentType.name), { id: map[$match.id] ? map[$match.id][0] : '', siteUrlName: $match.siteUrlName },
                { location: 'replace' });
            },
            () => {
              errorState.go({ detailsLocKey: 'pageDoesNotExist' });
            }
          );
      }]);

    // Site User

    // !!! ATTENTION !!!
    // If you changed the url scheme of user or user setting,
    // make sure to also update the user setting url generation code in:
    // \workgroup\vqlweb\scriptsharp\src\Authoring\ViewModels\AuthoringMastheadViewModel.cs

    $stateProvider
      .state('main.site.user', {
        abstract: true,
        url: '/user/:domainName/{username:.*}', // {username:.*} make sure username with forward slashes '/' are captured
        templateUrl: 'siteUserPlace.html',
        controller: SiteUserPlaceCtrl,
        resolve: {
          isSessionUserSelf: ['$stateParams',
                              'ServerService',
                              ($stateParams: UserStateParams,
                                serverService: ServerService) => {
            return serverService.isSessionUserEqualTo($stateParams.username, $stateParams.domainName);
          }],
          isSessionServerAdmin: ['ServerService',
                                 (serverService: ServerService) => {
            return serverService.isSessionServerAdmin();
          }],
          isSessionSiteAdmin: ['ServerService',
            (serverService: ServerService) => serverService.isSessionSiteAdmin()
          ],
          siteUser: ['SiteUsers',
                     'Users',
                     '$stateParams',
                     'isSessionUserSelf',
                     'isSessionServerAdmin',
                     'isSessionSiteAdmin',
                     (siteUsers: SiteUsers,
                      users: Users,
                      $stateParams: UserStateParams,
                      isSessionUserSelf: boolean,
                      isSessionServerAdmin: boolean,
                      isSessionSiteAdmin: boolean) => {
            const username = $stateParams.username;
            const domainName = $stateParams.domainName;
            if (isSessionUserSelf || isSessionServerAdmin || isSessionSiteAdmin) {
              // user is admin or viewing their own profile; fetch the data from getSiteUser
              return siteUsers.getUserByUsernameAndDomain(username, domainName);
            } else {
              // non-admin, non-self; fetch the data from getUser instead of getSiteUser.
              return users.getUserByUsernameAndDomain(username, domainName);
            }
          }],
          userSettings: ['UserSettingsActions',
                         '$stateParams',
                         'isSessionUserSelf',
                         'isSessionServerAdmin',
                         'isSessionSiteAdmin',
                         (userSettingsActions: UserSettingsActions,
                          $stateParams: UserStateParams,
                          isSessionUserSelf: boolean,
                          isSessionServerAdmin: boolean,
                          isSessionSiteAdmin: boolean) => {
            if (isSessionUserSelf || isSessionServerAdmin || isSessionSiteAdmin) {
              // user is admin or viewing their own profile; get the user's settings
              return userSettingsActions.fetchUserSettings($stateParams.username, $stateParams.domainName);
            } else {
              // non-admin, non-self; don't get settings.
              return null;
            }
          }],
          place: ['ErrorState', 'siteUser', 'SiteUserPlace', 'isSessionServerAdmin', 'isSessionUserSelf', 'ServerService',
              (errorState: ErrorState, siteUser: IUserInfo, SiteUserPlace: SiteUserPlace, isSessionServerAdmin: boolean, isSessionUserSelf: boolean, server: ServerService) => {
            if (siteUser) {
              // user exists on site
              return SiteUserPlace.create(siteUser.id);
            } else if (isSessionServerAdmin && isSessionUserSelf) {
              // Server admin who is not a member of the site
              return SiteUserPlace.empty();
            } else {
              // user doesn't exist on this site
              if (server.canSessionUserManageUsers()) {
                const usersErrorState: IErrorStateParams = {
                  detailsLocKey: 'userCannotBeAccessed',
                  redirectState: 'main.site.users',
                  redirectLocKey: 'backToUsers'
                };
                errorState.go(usersErrorState);
              } else {
                errorState.go();
              }
            }
          }],
          serverUser: ['isSessionServerAdmin',
                       '$stateParams',
                       'siteUser',
                       'ServerUsers',
                       (isSessionServerAdmin: boolean,
                        $stateParams: UserStateParams,
                        siteUser: ISiteUser,
                        serverUsers: ServerUsers) => {
            // we need to get the server user if the current user is a serverAdmin and not a site member
            if (!siteUser && isSessionServerAdmin) {
              return serverUsers.getUserByUsernameAndDomain($stateParams.username, $stateParams.domainName);
            } else {
              return null;
            }
          }]
        }
      })
      .state('main.site.user.projects',
        SubplaceStateConfigHelper.subplaceState({ name: 'projects' }))
      .state('main.site.user.workbooks',
        SubplaceStateConfigHelper.subplaceState({ name: 'workbooks' }))
      .state('main.site.user.views',
        SubplaceStateConfigHelper.subplaceState({ name: 'views' }))
      .state('main.site.user.datasources', {
        url: '/datasources',
        template: DatasourcesTemplate,
        controller: DatasourcesCtrl
      })
      .state('main.site.user.flows', {
        resolve: {
          subplace: ['place', (place: Place) => place.subplace('flows')]
        },
        url: '/flows',
        templateUrl: 'dataGridSubplaceWithFilterPanel.html',
        controller: FlowsCtrl
      })
      .state('main.site.user.settings', {
        url: '/settings',
        templateUrl: 'siteUserSettingsSubplace.html',
        controller: SiteUserSettingsSubplaceCtrl,
        resolve: {
          serverUser: ['ServerUsers', '$stateParams', (serverUsers: ServerUsers, $stateParams: UserStateParams) =>
            serverUsers.getUserByUsernameAndDomain($stateParams.username, $stateParams.domainName)
          ],
          userRefreshTokenCount: ['ServerUsers', '$stateParams', (serverUsers: ServerUsers, $stateParams: UserStateParams) =>
            serverUsers.getUserRefreshTokenCount($stateParams.username, $stateParams.domainName)
          ],
        },
        data: {
          authLevel: AuthLevel.SelfOrAdmin
        }
      })
      .state('main.site.user.subscriptions', {
        url: '/subscriptions',
        templateUrl: 'dataGridSubplaceWithoutFilterPanel.html',
        controller: SubscriptionsCtrl,
        resolve: {
          subplace: [ 'place', (place: Place) => place.subplace('subscriptions') ]
        },
        data: {
          authLevel: AuthLevel.SelfOrAdmin
        }
      });
    SubplaceStateConfigHelper.addDataAlertsState($stateProvider, 'main.site.user', { authLevel: AuthLevel.SelfOrAdmin });

    // Site content (aka All Content)
    if (FeatureFlags.homePage) {
      $stateProvider
        .state('main.site.home', {
          url: '/home',
          template: '<main><tb-connected-home></tb-connected-home></main>',
        });
    }

    // Site content (aka All Content)
    if (FeatureFlags.sandbox) {
      $stateProvider
        .state('main.site.sandbox', {
          url: '/sandbox',
          template: '<main><tb-connected-sandbox-space></tb-connected-sandbox-space></main>',
        });
    }

    // Metrics content
    if (FeatureFlags.metricsSpaceEnabled) {
      $stateProvider
        .state('main.site.metrics', {
          abstract: true,
          url: '/metrics',
          template: '<ui-view />',
        })
        .state('main.site.metrics.default',{
          url: '',
          template: '<main><tb-connected-metrics-space props="metricsSpaceProps"></tb-connected-metrics-space></main>',
          controller: MetricsSpaceCtrl,
          resolve: {
            subspace: () => 'favorites'
          },
        })
        .state('main.site.metrics.all',{
          url: '/all',
          template: '<main><tb-connected-metrics-space props="metricsSpaceProps"></tb-connected-metrics-space></main>',
          controller: MetricsSpaceCtrl,
          resolve: {
            subspace: () => 'all'
          },
        })
        .state('main.site.metrics.owned',{
          url: '/owned',
          template: '<main><tb-connected-metrics-space props="metricsSpaceProps"></tb-connected-metrics-space></main>',
          controller: MetricsSpaceCtrl,
          resolve: {
            subspace: () => 'owned'
          },
        })
        .state('main.site.metrics.favorites',{
          url: '/favorites',
          template: '<main><tb-connected-metrics-space props="metricsSpaceProps"></tb-connected-metrics-space></main>',
          controller: MetricsSpaceCtrl,
          resolve: {
            subspace: () => 'favorites'
          },
        });
    }

    $stateProvider
      .state('main.site.content', {
        url: '',
        template: '<div class="tb-fill" ui-view></div>',
        abstract: true
      })
      .state('main.site.content.site', {
        abstract: true,
        template: '<tb:connected-site-space props="props"/>',
        controller: SitePlaceCtrl
      })
      // All of the substates for main.content.site share the SitePlaceCtrl, but the states are explicitly listed below
      // so the router knows which URLs are valid.
      .state('main.site.content.site.explore', {
        url: '/' + SiteSpaceConfigHelper.siteSpaceUrlSuffix('explore'),
      })
      .state('main.site.content.site.projects', {
        url: '/' + SiteSpaceConfigHelper.siteSpaceUrlSuffix('project'),
      })
      .state('main.site.content.site.workbooks', {
        url: '/' + SiteSpaceConfigHelper.siteSpaceUrlSuffix('workbook'),
      })
      .state('main.site.content.site.views', {
        url: '/' + SiteSpaceConfigHelper.siteSpaceUrlSuffix('view'),
      })
      .state('main.site.content.site.datasources', {
        url: '/' + SiteSpaceConfigHelper.siteSpaceUrlSuffix('datasource'),
      })
      .state('main.site.content.site.flows', {
        url: '/' + SiteSpaceConfigHelper.siteSpaceUrlSuffix('flow'),
      })
      .state('main.site.content.site.dataRoles', {
        url: '/' + SiteSpaceConfigHelper.siteSpaceUrlSuffix('dataRole'),
      })
      .state('main.site.content.site.metrics', {
        url: '/metrics', // Once Metrics is a Spaces content type, use the siteSpaceUrlSuffix function
        templateUrl: 'metrics.html',
        controller: MetricsCtrl,
        resolve: {
          subplace: ['place', (place: Place) => place.subplace('metrics')],
        },
      });
    // Project details

    $stateProvider
      .state('main.site.content.project', {
        url: '/projects/:id',
        template: '<tb:connected-project-space props="projectSpaceProps" />',
        resolve: {
          projectDetailInfo: ['Projects', '$stateParams', (Projects: Projects, $stateParams: IDetailContentParams) => {
            return Projects.getById($stateParams.id);
          }],
          projectActions: ['Projects', '$stateParams', (Projects: Projects, $stateParams: IDetailContentParams) => {
            return Projects.actions($stateParams.id);
          }],
          projectHierarchy: ['Projects', '$stateParams', (Projects: Projects, $stateParams: IDetailContentParams) => {
            return Projects.getProjectHierarchy($stateParams.id);
          }],
          projectInfo: ['Projects', '$stateParams', (Projects: Projects, $stateParams: IDetailContentParams) => {
            return Projects.all()
              .withFilters([Filter.fieldEq('id', $stateParams.id)])
              .getFirst();
          }],
        },
        controller: ProjectPlaceCtrl,
        data: {
          errorState: {
            detailsLocKey: 'projectCannotBeAccessed',
            redirectState: 'main.site.content.site.projects',
            redirectLocKey: 'backToProjects'
          }
        }
      });

    // DataRole details
    $stateProvider.state('main.site.content.dataRole', {
      url: '/dataRoles/:id',
      templateUrl: 'dataRole.html',
      abstract: true,
      resolve: {
        dataRoleAllowedActions: ['DataRoles', '$stateParams', (DataRoles: DataRoles, $stateParams: IDetailContentParams) => {
          return DataRoles.actions($stateParams.id);
        }],
        dataRoleDetailInfo: ['DataRoles', '$stateParams', (DataRoles: DataRoles, $stateParams: IDetailContentParams) => {
          return DataRoles.getById($stateParams.id);
        }],
        dataRoleListItemInfo: ['DataRoles', '$stateParams', (DataRoles: DataRoles, $stateParams: IDetailContentParams) => {
          return DataRoles.all()
            .withFilters([Filter.fieldEq('id', $stateParams.id)])
            .getFirst();
        }],
        projectHierarchy: ['dataRoleListItemInfo', 'Projects', (dataRoleListItemInfo: IDataRoleInfo, Projects: Projects) => {
          return Projects.getProjectHierarchy(dataRoleListItemInfo.project.id);
        }]
      },
      controller: DataRolePlaceCtrl,
    })
    .state('main.site.content.dataRole.definition', {
      url: '/definition',
      templateUrl: 'dataRole.html',
      controller: setBrowserTitleCtrl('definition'),
      data: {
        subplaceName: 'definition',
      },
    });

    // Flow details
    $stateProvider.state('main.site.content.flow', {
      url: '/flows/:id',
      templateUrl: 'flow.html',
      abstract: true,
      resolve: {
        flowAllowedActions: ['Flows', '$stateParams', (Flows: Flows, $stateParams: IDetailContentParams) => {
          return Flows.actions($stateParams.id);
        }],
        flowDetailInfo: ['Flows', '$stateParams', (Flows: Flows, $stateParams: IDetailContentParams) => {
          return Flows.getById($stateParams.id);
        }],
        flowListItemInfo: ['Flows', '$stateParams', (Flows: Flows, $stateParams: IDetailContentParams) => {
          return Flows.all()
            .withFilters([Filter.fieldEq('id', $stateParams.id)])
            .getFirst();
        }],
        place: ['flowListItemInfo', 'FlowPlace', (flowListItemInfo: FlowListItemInfo, FlowPlace: FlowPlace) => {
          return FlowPlace.create(flowListItemInfo.id);
        }],
        projectHierarchy: ['flowListItemInfo', 'Projects', (flowListItemInfo: FlowListItemInfo, Projects: Projects) => {
          return Projects.getProjectHierarchy(flowListItemInfo.project.id);
        }]
      },
      controller: FlowPlaceCtrl,
      data: {
        errorState: {
          detailsLocKey: 'flowCannotBeAccessed',
          redirectState: 'main.site.content.site.flows',
          redirectLocKey: 'backToFlows'
        }
      }
    })
    .state('main.site.content.flow.overview', {
      url: '/overview',
      templateUrl: 'flowOverview.html',
      controller: setBrowserTitleCtrl('overview'),
      data: {
        subplaceName: 'overview',
      },
    })
    .state('main.site.content.flow.connections', {
      url: '/connections',
      templateUrl: 'flowConnections.html',
      controller: setBrowserTitleCtrl('connections'),
      data: {
        subplaceName: 'connections',
      },
    })
    .state('main.site.content.flow.scheduledTasks', {
      url: '/scheduledTasks',
      templateUrl: 'flowRunSchedules.html',
      controller: setBrowserTitleCtrl('scheduledTasks'),
      data: {
        subplaceName: 'scheduledTasks',
      },
    })
    .state('main.site.content.flow.runHistory', {
      url: '/runHistory',
      templateUrl: 'flowRunHistory.html',
      controller: setBrowserTitleCtrl('runHistory'),
      data: {
        subplaceName: 'runHistory',
      },
    })
    .state('main.site.content.flow.lineage', {
      url: '/lineage',
      controller: setBrowserTitleCtrl('lineage'),
      data: {
        subplaceName: 'lineage',
      }
    })
    .state('main.site.content.flow.lineage.subpath', {
      url: '/*path',
    })
    .state('main.site.content.flow.publishresult', {
      url: '/publishresult',
      controller: FlowPublishResultCtrl,
      data: {
        subplaceName: 'publishresult',
      },
    });

    // Workbook details
    $stateProvider
    .state('main.site.content.workbook', {
      url: '/workbooks/:id',
      abstract: false,
      template: '<tb:workbook-space-router />', // WorkbookSpaceRouter.tsx via angularDirectives.ts
    }).state('main.site.content.workbook.subspace', {
      url: '/*subspace'
    });

    // Datasource details

    $stateProvider
      .state('main.site.content.datasource', {
        url: '/datasources/:id',
        templateUrl: 'datasource.html',
        abstract: true,
        resolve: {
          datasourceDetailInfo: ['DataSources', '$stateParams', (DataSources: DataSources, $stateParams: IDetailContentParams) => {
            return DataSources.getById($stateParams.id);
          }],
          datasourceActions: ['DataSources', '$stateParams', (DataSources: DataSources, $stateParams: IDetailContentParams) => {
            return DataSources.actions($stateParams.id);
          }],
          datasourceInfo: ['DataSources', '$stateParams', (DataSources: DataSources, $stateParams: IDetailContentParams) => {
            return DataSources.all()
              .withFilters([Filter.fieldEq('id', $stateParams.id)])
              .getFirst();
          }],
          projectHierarchy: ['datasourceDetailInfo', 'Projects', (datasourceDetailInfo: IDataSourceDetailInfo, Projects: Projects) => {
            return Projects.getProjectHierarchy(datasourceDetailInfo.project.id);
          }],
        },
        controller: DatasourcePlaceCtrl,
        data: {
          errorState: {
            detailsLocKey: 'datasourceCannotBeAccessed',
            redirectState: 'main.site.content.datasource',
            redirectLocKey: 'backToDatasources'
          }
        }
      })
      .state('main.site.content.datasource.askDataUsageAnalytics', {
        url: '/askDataUsageAnalytics',
        templateUrl: 'datasource.html',
      })
      .state('main.site.content.datasource.connections', {
        url: '/connections',
      })
      .state('main.site.content.datasource.connectedWorkbooks', {
        url: '/connectedWorkbooks'
      })
      .state('main.site.content.datasource.nlp',{
        url: '/askData',
        templateUrl: 'nlp.html',
        controller: NaturalLanguageCtrl,
      })
      .state('main.site.content.datasource.lineage', {
        url: '/lineage',
      })
      .state('main.site.content.datasource.lineage.subpath', {
        url: '/*path',
        data: {
          subplaceName: 'lineage',
        }
      })
      .state('main.site.content.datasource.remoteRefreshSchedules', {
        url: '/localExtractRefreshes'
      })
      .state('main.site.content.datasource.extractTasks', {
        url: '/extractRefreshes'
      })
      .state('main.site.content.datasource.publishresult', {
        url: '/publishresult',
        resolve: {
          preferredRefreshOption: ['DataSources', 'datasourceDetailInfo',
          (DataSources: DataSources, datasourceDetailInfo: IDataSourceDetailInfo) => {
            return DataSources.getPreferredRefreshOption(datasourceDetailInfo.id);
          }]
        },
        controller: DatasourcePublishResultCtrl
      });

    // favorites
    $stateProvider
    .state('main.site.content.favorites', {
      url: '/favorites',
      template: '<tb:connected-favorites-space props="favoritesSpaceProps" />',
      abstract: false,
      controller: FavoritesSpaceCtrl,
    });

    // recents
    $stateProvider
    .state('main.site.content.recents', {
      url: '/recents',
      template: '<tb:recents-space props="recentsSpaceProps" />',
      abstract: false,
      controller: RecentsSpaceCtrl,
    });

    // recommendations
    $stateProvider
    .state('main.site.content.recommendations', {
      url: '/recommendations',
      template: '<tb:recommendations-space props="recommendationsSpaceProps" />',
      abstract: false,
      controller: RecommendationsSpaceCtrl,
    });

    // search
    if (FeatureFlags.searchSpace) {
      $stateProvider.state('main.site.content.search',
        {
          url: '/search',
          template: '<tb:search-space/>',
          abstract: false
        }
      ).state('main.site.content.search.subspace', {
        url: '/:subspace'
      });
    }
  }
]);
